/**
 * Realtor CRM API service — contacts book, message threads, enquiries (#332).
 *
 * Client for /api/v1/realtor-crm/*, using the same `ApiResult` envelope as the
 * leads service. Messages are a log the realtor keeps: the other person has no
 * Finishd account, so an "outbound" message is what the realtor sent through
 * WhatsApp or the dialler and an "inbound" one is a reply they recorded.
 */

import { apiClient } from "@/lib/api-client";
import {
	type ActivityType,
	type ApiResult,
	buildFailure,
	type Lead,
	type LeadTemperature,
	type LeadType,
	type PipelineStage,
} from "./leads-api.service";

const BASE = "/api/v1/realtor-crm";

export type ContactType =
	| "buyer"
	| "seller"
	| "owner"
	| "builder"
	| "contractor";
export const CONTACT_TYPES: readonly ContactType[] = [
	"buyer",
	"seller",
	"owner",
	"builder",
	"contractor",
];

export type MessageDirection = "outbound" | "inbound";
export type MessageChannel =
	| "whatsapp"
	| "call"
	| "sms"
	| "email"
	| "in_person"
	| "other";
export type EnquiryChannel =
	| "whatsapp"
	| "referral"
	| "phone_call"
	| "walk_in"
	| "other";
export const ENQUIRY_CHANNELS: readonly EnquiryChannel[] = [
	"whatsapp",
	"referral",
	"phone_call",
	"walk_in",
	"other",
];
export type EnquiryStatus = "open" | "converted" | "dismissed";

export interface Contact {
	id: string;
	realtorId: string;
	name: string;
	type: ContactType;
	phoneCountryCode: string;
	/** Digits only. */
	phone: string | null;
	email: string | null;
	city: string | null;
	notes: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface ContactSummary {
	contact: Contact;
	leadCount: number;
}

export type ContactCounts = Record<ContactType | "all", number>;

export interface ContactList {
	contacts: ContactSummary[];
	/** Per type for the current search, ignoring the type filter. */
	counts: ContactCounts;
}

export interface ContactDetail {
	contact: Contact;
	leads: Array<
		Pick<Lead, "id" | "name" | "type" | "pipelineStage" | "temperature">
	>;
}

export interface ContactInput {
	name: string;
	type: ContactType;
	phone?: string | null;
	phoneCountryCode?: string;
	email?: string | null;
	city?: string | null;
	notes?: string | null;
}

export interface CrmMessage {
	id: string;
	realtorId: string;
	contactId: string | null;
	enquiryId: string | null;
	direction: MessageDirection;
	channel: MessageChannel;
	body: string;
	occurredAt: string;
	createdAt: string;
}

export interface MessageInput {
	direction: MessageDirection;
	channel: MessageChannel;
	body: string;
	/** ISO timestamp; defaults to now on the server. */
	occurredAt?: string;
}

export interface ContactMessageInput extends MessageInput {
	/** The deal this conversation was about; must be one of the contact's. */
	leadId?: string | null;
}

export interface TimelineLead {
	id: string;
	name: string;
	type: LeadType;
}

/**
 * One thing that happened with a person: a logged or imported message, or an
 * activity on one of their leads. Mirrors the backend's `TimelineEntry`.
 */
export interface TimelineEntry {
	kind: "message" | "activity";
	id: string;
	/** Message: when it was sent. Activity: completed, else scheduled, else
	 * created. */
	occurredAt: string;
	createdAt: string;
	/** Null for activities. */
	direction: MessageDirection | null;
	/** Null for activities. */
	channel: MessageChannel | null;
	/** Null for messages. */
	activityType: ActivityType | null;
	/** Null for messages. */
	title: string | null;
	body: string | null;
	lead: TimelineLead | null;
	/**
	 * Always `manual` for activities. `whatsapp` was recorded from the
	 * realtor's connected number as it was sent; `whatsapp_history` came from
	 * the earlier chats shared at connect or from the review list.
	 */
	source: "manual" | "import" | "whatsapp" | "whatsapp_history";
	/** Position in the imported file; null unless imported. */
	importOrdinal: number | null;
	scheduledAt: string | null;
	completedAt: string | null;
}

export interface TimelinePage {
	/** Oldest first within the page. */
	entries: TimelineEntry[];
	/** Pass as `before` for the next older page; null when there is none. */
	nextBefore: string | null;
}

export interface Thread {
	contact: Contact;
	lastMessage: CrmMessage;
	messageCount: number;
	/** The last message in the thread came from them. */
	awaitingReply: boolean;
}

export interface Enquiry {
	id: string;
	realtorId: string;
	contactId: string | null;
	listingId: string | null;
	name: string;
	phoneCountryCode: string;
	phone: string | null;
	channel: EnquiryChannel;
	budgetMin: number | null;
	budgetMax: number | null;
	intent: string | null;
	status: EnquiryStatus;
	convertedLeadId: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface EnquiryListing {
	id: string;
	headline: string;
	locality: string;
	city: string;
}

export interface EnquirySummary {
	enquiry: Enquiry;
	listing: EnquiryListing | null;
	firstMessage: CrmMessage | null;
	messageCount: number;
}

export interface EnquiryDetail {
	enquiry: Enquiry;
	listing: EnquiryListing | null;
	messages: CrmMessage[];
}

export interface EnquiryInput {
	name: string;
	phone?: string | null;
	phoneCountryCode?: string;
	channel: EnquiryChannel;
	listingId?: string | null;
	/** Paise. */
	budgetMin?: number | null;
	budgetMax?: number | null;
	intent?: string | null;
	/** What they said; recorded as the first message. */
	message: string;
}

export interface ConvertEnquiryInput {
	name: string;
	phone: string;
	phoneCountryCode?: string;
	type: LeadType;
	pipelineStage?: PipelineStage;
	budgetMin?: number | null;
	budgetMax?: number | null;
	notes?: string | null;
}

export interface ConvertedEnquiry {
	enquiry: Enquiry;
	lead: Lead;
}

async function call<T>(
	fallback: string,
	request: () => Promise<{ data: { data: T } }>,
): Promise<ApiResult<T>> {
	try {
		const response = await request();
		return { success: true, data: response.data.data };
	} catch (error: unknown) {
		return buildFailure<T>(error, fallback);
	}
}

// Contacts --------------------------------------------------------------------

export function listContacts(filter: {
	q?: string;
	type?: ContactType;
}): Promise<ApiResult<ContactList>> {
	return call("Failed to load contacts", () =>
		apiClient.get(`${BASE}/contacts`, {
			params: {
				...(filter.q ? { q: filter.q } : {}),
				...(filter.type ? { type: filter.type } : {}),
			},
		}),
	);
}

export function getContact(id: string): Promise<ApiResult<ContactDetail>> {
	return call("Failed to load contact", () =>
		apiClient.get(`${BASE}/contacts/${id}`),
	);
}

export async function createContact(
	input: ContactInput,
): Promise<ApiResult<Contact>> {
	const result = await call<{ contact: Contact }>(
		"Failed to save contact",
		() => apiClient.post(`${BASE}/contacts`, input),
	);
	return { ...result, data: result.data?.contact };
}

export async function updateContact(
	id: string,
	input: Partial<ContactInput>,
): Promise<ApiResult<Contact>> {
	const result = await call<{ contact: Contact }>(
		"Failed to save contact",
		() => apiClient.patch(`${BASE}/contacts/${id}`, input),
	);
	return { ...result, data: result.data?.contact };
}

export function deleteContact(id: string): Promise<ApiResult<null>> {
	return call("Failed to delete contact", () =>
		apiClient.delete(`${BASE}/contacts/${id}`),
	);
}

// Messages --------------------------------------------------------------------

export async function listThreads(): Promise<ApiResult<Thread[]>> {
	const result = await call<{ threads: Thread[] }>(
		"Failed to load messages",
		() => apiClient.get(`${BASE}/threads`),
	);
	return { ...result, data: result.data?.threads };
}

/**
 * One page of a contact's timeline: the newest page without `before`, and
 * the page just older than that cursor with it.
 */
export function getContactTimeline(
	contactId: string,
	before?: string,
): Promise<ApiResult<TimelinePage>> {
	return call("Failed to load the conversation", () =>
		apiClient.get(`${BASE}/contacts/${contactId}/timeline`, {
			params: before ? { before } : {},
		}),
	);
}

export async function logContactMessage(
	contactId: string,
	input: ContactMessageInput,
): Promise<ApiResult<CrmMessage>> {
	const result = await call<{ message: CrmMessage }>(
		"Failed to save message",
		() => apiClient.post(`${BASE}/contacts/${contactId}/messages`, input),
	);
	return { ...result, data: result.data?.message };
}

// Follow-ups ------------------------------------------------------------------

/** Mirrors the backend's `FollowUpReasonType`, highest priority first. */
export type FollowUpReasonType =
	| "reply_waiting"
	| "reminder_due"
	| "viewing_feedback"
	| "gone_quiet"
	| "listing_match";

export interface FollowUpContact {
	id: string;
	name: string;
	/** Digits only. */
	phone: string | null;
	phoneCountryCode: string;
}

export interface FollowUpReason {
	type: FollowUpReasonType;
	leadId: string | null;
	/** How long this reason has been waiting. Negative for a reminder due
	 * later today: the time until it is due. */
	waitingMs: number;
	details: Record<string, string | null>;
}

export interface FollowUpLead {
	id: string;
	name: string;
	type: LeadType;
	stage: PipelineStage;
	temperature: LeadTemperature;
}

/** One person in the queue, however many reasons apply to them. */
export interface FollowUpEntry {
	contact: FollowUpContact;
	leadingReason: FollowUpReasonType;
	waitingMs: number;
	/** Every reason that applies, leading reason first. */
	reasons: FollowUpReason[];
	/** Plain text for WhatsApp, written for the leading reason. */
	readyMessage: string;
	/** The person's open leads. */
	leads: FollowUpLead[];
}

export interface SnoozedContact {
	contact: FollowUpContact;
	/** The India date the snooze lasts until, `YYYY-MM-DD`. */
	until: string;
}

export interface FollowUpList {
	/** Ordered by reason priority, then the longest wait first. */
	followUps: FollowUpEntry[];
	snoozed: SnoozedContact[];
}

export function listFollowUps(): Promise<ApiResult<FollowUpList>> {
	return call("Couldn't load your follow-ups", () =>
		apiClient.get(`${BASE}/follow-ups`),
	);
}

/** Hides the person's nudge until the start of the India date `until`. */
export function snoozeFollowUp(
	contactId: string,
	until: string,
): Promise<ApiResult<{ contactId: string; until: string }>> {
	return call("Couldn't snooze this follow-up", () =>
		apiClient.post(`${BASE}/contacts/${contactId}/snooze`, { until }),
	);
}

/** "Not a fit": the listing stops matching the lead. Safe to repeat. */
export function dismissListingMatch(
	leadId: string,
	listingId: string,
): Promise<ApiResult<{ leadId: string; listingId: string }>> {
	return call("Couldn't mark the listing as not a fit", () =>
		apiClient.post(`${BASE}/leads/${leadId}/listings/${listingId}/dismiss`),
	);
}

/**
 * Whether WhatsApp recorded an outbound message to the person at or after
 * `since` (R21). Fails with 404 while WhatsApp capture is off, or when the
 * contact is not the realtor's.
 */
export function checkWhatsappSent(
	contactId: string,
	since: string,
): Promise<ApiResult<{ sent: boolean }>> {
	return call("Couldn't check WhatsApp", () =>
		apiClient.get(`${BASE}/contacts/${contactId}/whatsapp-sent`, {
			params: { since },
		}),
	);
}

export function clearFollowUpSnooze(
	contactId: string,
): Promise<ApiResult<null>> {
	return call("Couldn't unsnooze this follow-up", () =>
		apiClient.delete(`${BASE}/contacts/${contactId}/snooze`),
	);
}

// Chat import -----------------------------------------------------------------

/** How the export's dates read: 03/04 as 3 April, or as 4 March. */
export type ImportDateOrder = "day-first" | "month-first";

/**
 * `certain`: a value above 12 decided it. `inferred`: the order the values
 * change in decided it. `defaulted`: nothing decided it, so day-first was
 * assumed. `explicit`: the request chose it.
 */
export type ImportDateOrderConfidence =
	| "certain"
	| "inferred"
	| "defaulted"
	| "explicit";

/** What `POST /imports/preview` reports about an uploaded chat export. */
export interface ChatImportPreview {
	platform: "android" | "ios";
	messageCount: number;
	authors: Array<{ name: string; messageCount: number }>;
	/** ISO timestamps, read with `dateOrder.order`. */
	dateRange: { first: string; last: string };
	dateOrder: {
		order: ImportDateOrder;
		confidence: ImportDateOrderConfidence;
	};
	/** Lines that start like a date but are not a message header. */
	warningLines: number[];
	/** An author whose display name is a number the phone has not saved. */
	phoneSuggestion: {
		author: string;
		phone: string;
		phoneCountryCode: string;
	} | null;
	/** The realtor's contact with the suggested phone, if there is one. */
	matchingContact: { id: string; name: string; type: ContactType } | null;
	/** Set only when the preview was asked about a contact. */
	alreadyImportedCount: number | null;
	/** The order this contact's last import used, if any. */
	previousDateOrder: ImportDateOrder | null;
}

/**
 * Either `contactId`, or a new contact's `name` plus `type` (the server uses
 * the lead's type when `type` is left out and a lead is requested).
 */
export interface ChatImportInput {
	selfAuthor: string;
	dateOrder: ImportDateOrder;
	contactId?: string;
	name?: string;
	type?: ContactType;
	phone?: string;
	phoneCountryCode?: string;
	/** A new contact only, and only with `phone`. */
	lead?: { type: LeadType; temperature?: LeadTemperature };
}

export interface ChatImportResult {
	imported: number;
	/** Messages already on the contact's timeline. */
	skipped: number;
	contactId: string;
	leadId: string | null;
	importId: string;
}

function chatExportForm(
	file: File,
	fields: Record<string, string | undefined>,
): FormData {
	const form = new FormData();
	form.append("file", file);
	for (const [key, value] of Object.entries(fields)) {
		if (value !== undefined) form.append(key, value);
	}
	return form;
}

/** Reads a chat export and reports what is in it; saves nothing. */
export function previewChatImport(
	file: File,
	options: { contactId?: string } = {},
): Promise<ApiResult<ChatImportPreview>> {
	return call("Couldn't read this chat export", () =>
		apiClient.post(
			`${BASE}/imports/preview`,
			chatExportForm(file, { contactId: options.contactId }),
		),
	);
}

/** Uploads the same file again with the realtor's choices and saves it. */
export function commitChatImport(
	file: File,
	input: ChatImportInput,
): Promise<ApiResult<ChatImportResult>> {
	return call("Couldn't import this chat", () =>
		apiClient.post(
			`${BASE}/imports`,
			chatExportForm(file, {
				selfAuthor: input.selfAuthor,
				dateOrder: input.dateOrder,
				contactId: input.contactId,
				name: input.name,
				type: input.type,
				phone: input.phone,
				phoneCountryCode: input.phoneCountryCode,
				"lead[type]": input.lead?.type,
				"lead[temperature]": input.lead?.temperature,
			}),
		),
	);
}

// Enquiries -------------------------------------------------------------------

export async function listEnquiries(filter: {
	status?: EnquiryStatus;
}): Promise<ApiResult<EnquirySummary[]>> {
	const result = await call<{ enquiries: EnquirySummary[] }>(
		"Failed to load enquiries",
		() =>
			apiClient.get(`${BASE}/enquiries`, {
				params: filter.status ? { status: filter.status } : {},
			}),
	);
	return { ...result, data: result.data?.enquiries };
}

export function getEnquiry(id: string): Promise<ApiResult<EnquiryDetail>> {
	return call("Failed to load enquiry", () =>
		apiClient.get(`${BASE}/enquiries/${id}`),
	);
}

export function createEnquiry(
	input: EnquiryInput,
): Promise<ApiResult<EnquiryDetail>> {
	return call("Failed to log enquiry", () =>
		apiClient.post(`${BASE}/enquiries`, input),
	);
}

export async function logEnquiryMessage(
	enquiryId: string,
	input: MessageInput,
): Promise<ApiResult<CrmMessage>> {
	const result = await call<{ message: CrmMessage }>(
		"Failed to save message",
		() => apiClient.post(`${BASE}/enquiries/${enquiryId}/messages`, input),
	);
	return { ...result, data: result.data?.message };
}

export async function dismissEnquiry(id: string): Promise<ApiResult<Enquiry>> {
	const result = await call<{ enquiry: Enquiry }>(
		"Failed to dismiss enquiry",
		() => apiClient.post(`${BASE}/enquiries/${id}/dismiss`),
	);
	return { ...result, data: result.data?.enquiry };
}

export function convertEnquiry(
	id: string,
	input: ConvertEnquiryInput,
): Promise<ApiResult<ConvertedEnquiry>> {
	return call("Failed to convert enquiry", () =>
		apiClient.post(`${BASE}/enquiries/${id}/convert`, input),
	);
}

// Hand-offs -------------------------------------------------------------------

/**
 * A wa.me link that opens WhatsApp on this number with `text` prefilled.
 * Null when there is no number to open.
 */
export function whatsappUrl(
	phoneCountryCode: string,
	phone: string | null,
	text?: string,
): string | null {
	const digits = phone?.replace(/\D/g, "");
	if (!digits) return null;
	const base = `https://wa.me/${phoneCountryCode}${digits}`;
	return text ? `${base}?text=${encodeURIComponent(text)}` : base;
}

/** A tel: link for the dialler, or null when there is no number. */
export function telUrl(
	phoneCountryCode: string,
	phone: string | null,
): string | null {
	const digits = phone?.replace(/\D/g, "");
	return digits ? `tel:+${phoneCountryCode}${digits}` : null;
}
